/**
 * Bloquear agendas: días enteros o unas horas (antes «Agendas bloqueadas»,
 * 2026-08-26, dirección — URGENTE: festivos, congresos…).
 *
 * Desde el 14/10/2026 no es un botón de la Agenda con su ventana: es una
 * sección de la página «Agendas disponibles» (pages/AgendasDisponiblesPage),
 * con el mismo formulario de siempre. Se eligen días en el calendario
 * (sueltos o seguidos), las agendas afectadas (una, varias, las del doctor
 * elegido o todas), el motivo y, si no es el día entero, las horas; el
 * servidor crea los bloqueos (/api/agendas/bloqueos-lote, el backend de
 * siempre) y desde ese momento es imposible citar ahí (lo impone el backend,
 * no solo la vista). Abajo, los bloqueos vigentes con su botón de quitar.
 *
 * Las vacaciones y los días libres de un doctor, y los bloqueos que salen de
 * un patrón, no se listan aquí: se ven y se quitan en el calendario del
 * doctor (son de él, no de una agenda suelta).
 *
 * Si esos días ya hay citas, el servidor no bloquea nada y dice cuántas
 * (409): se pregunta y, si se sigue, se bloquea igual. Las citas no se anulan.
 *
 * Los motivos que se ofrecen son genéricos («Festivo», «Congreso») más «Otro
 * motivo…» para escribirlo: nada de la clínica escrito en el código (no hay
 * una lista de motivos en Configuración; si la hubiera, saldría de ahí).
 *
 * Props:
 *  - agendas: las agendas que se pueden bloquear (las de GET /api/agendas);
 *  - destacadas: {nombre, ids} del doctor elegido arriba, para un atajo
 *    «Las de <doctor>» (opcional);
 *  - recarga: un número que la página sube tras cualquier cambio (vacaciones,
 *    un patrón, un bloqueo desde el calendario…): la lista de abajo se vuelve
 *    a pedir y no se queda vieja. Con él, tras un cambio propio basta con
 *    avisar a la página (onChanged) y la lista se pide una sola vez;
 *  - onChanged(): tras crear o quitar un bloqueo.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2, Lock, Trash2 } from 'lucide-react';
import { formatDate, mensajeError } from '../../lib/api';
import { listBloqueos, deleteBloqueo, esDeAgendasDisponibles, toMin } from '../../lib/citasApi';
import { bloquearAgendas, citasPorConfirmar, textoHoras } from '../../lib/disponibilidad';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

// Las vacaciones del médico ya no van aquí: se marcan en su calendario, que
// bloquea todas sus agendas y las agrupa para quitarlas juntas. Solo motivos
// genéricos: cualquier otro se escribe con «Otro motivo…».
export const MOTIVOS = ['Festivo', 'Congreso'];
const DIAS_SEMANA = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Los que se gestionan en el calendario del doctor (vacaciones, días
 *  libres y los de un patrón), no en esta lista. */
const esDelCalendario = esDeAgendasDisponibles;

export default function BloqueosAgendaPanel({ agendas = [], destacadas = null, recarga, onChanged }) {
  const controlado = recarga !== undefined;
  const hoy = useMemo(() => new Date(), []);
  const [mes, setMes] = useState(() => new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  const [seleccion, setSeleccion] = useState(() => new Set()); // 'YYYY-MM-DD'
  const [agendasSel, setAgendasSel] = useState(() => new Set());
  const [motivo, setMotivo] = useState('Festivo');
  const [motivoLibre, setMotivoLibre] = useState('');
  const [diaEntero, setDiaEntero] = useState(true);
  const [start, setStart] = useState('15:00');
  const [end, setEnd] = useState('20:00');
  const [guardando, setGuardando] = useState(false);
  const [existentes, setExistentes] = useState([]); // bloqueos vigentes (todas las agendas)
  const [cargandoExistentes, setCargandoExistentes] = useState(false);

  const nombreAgenda = useMemo(() => {
    const m = {};
    agendas.forEach((a) => { m[a.id] = a.name; });
    return m;
  }, [agendas]);

  // Bloqueos vigentes o futuros de TODAS las agendas, para verlos y quitarlos.
  const cargarExistentes = useCallback(async () => {
    if (!agendas.length) return;
    setCargandoExistentes(true);
    try {
      const desde = ymd(new Date());
      const listas = await Promise.all(
        agendas.map((a) => listBloqueos(a.id, { dateFrom: desde }).catch(() => [])),
      );
      const todos = listas.flat()
        .filter((b) => b && (b.tipo || 'cierre') !== 'apertura' && !esDelCalendario(b));
      todos.sort((x, y) => (x.date_from || '').localeCompare(y.date_from || '')
        || (x.start || '').localeCompare(y.start || ''));
      setExistentes(todos);
    } finally {
      setCargandoExistentes(false);
    }
  }, [agendas]);

  // También cuando la página cambia algo (`recarga`): la lista no se queda vieja.
  useEffect(() => { cargarExistentes(); }, [cargarExistentes, recarga]);

  /** Tras un cambio propio: con `recarga`, la página la sube y la lista se
   *  pide sola; sin ella, se pide aquí. */
  const trasCambiar = async () => {
    if (controlado) onChanged?.();
    else {
      await cargarExistentes();
      onChanged?.();
    }
  };

  // Días de este mes que YA tienen algún bloqueo (para pintarlos en el calendario).
  const bloqueadosDelMes = useMemo(() => {
    const set = new Set();
    const y = mes.getFullYear(); const m = mes.getMonth();
    const ultimo = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= ultimo; d += 1) {
      const f = ymd(new Date(y, m, d));
      if (existentes.some((b) => b.date_from <= f && f <= b.date_to)) set.add(f);
    }
    return set;
  }, [existentes, mes]);

  const alternarDia = (f) => setSeleccion((prev) => {
    const next = new Set(prev);
    if (next.has(f)) next.delete(f); else next.add(f);
    return next;
  });
  const alternarAgenda = (id) => setAgendasSel((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const idsDestacadas = (destacadas?.ids || []).filter((id) => nombreAgenda[id]);
  const sonLasDestacadas = idsDestacadas.length > 0 && agendasSel.size === idsDestacadas.length
    && idsDestacadas.every((id) => agendasSel.has(id));

  const semanas = useMemo(() => {
    const y = mes.getFullYear(); const m = mes.getMonth();
    const primero = new Date(y, m, 1);
    // Lunes = 0
    const offset = (primero.getDay() + 6) % 7;
    const ultimo = new Date(y, m + 1, 0).getDate();
    const celdas = [];
    for (let i = 0; i < offset; i += 1) celdas.push(null);
    for (let d = 1; d <= ultimo; d += 1) celdas.push(new Date(y, m, d));
    while (celdas.length % 7 !== 0) celdas.push(null);
    const out = [];
    for (let i = 0; i < celdas.length; i += 7) out.push(celdas.slice(i, i + 7));
    return out;
  }, [mes]);

  const motivoFinal = motivo === '__otro__' ? motivoLibre.trim() : motivo;
  const horasMal = !diaEntero && (!start || !end || toMin(end) <= toMin(start));

  const bloquear = async () => {
    if (!seleccion.size) return toast.error('Elige al menos un día en el calendario');
    if (!agendasSel.size) return toast.error('Elige a qué agendas aplica el bloqueo');
    if (!motivoFinal) return toast.error('Indica el motivo del bloqueo');
    if (horasMal) return toast.error('La hora de fin tiene que ser posterior a la de inicio');
    setGuardando(true);
    const cuerpo = {
      agendaIds: Array.from(agendasSel),
      fechas: Array.from(seleccion),
      motivo: motivoFinal,
      ...(diaEntero ? {} : { start, end }),
    };
    try {
      let r;
      try {
        r = await bloquearAgendas(cuerpo);
      } catch (e) {
        // Esos días ya hay citas: se pregunta antes de bloquear (no se anulan).
        const citas = citasPorConfirmar(e);
        if (!citas) throw e;
        if (!window.confirm(`${citas.detail}\n\n¿Bloquear igualmente?`)) return undefined;
        r = await bloquearAgendas({ ...cuerpo, confirmar: true });
      }
      const horas = diaEntero ? '' : ` de ${start} a ${end}`;
      toast.success(`${seleccion.size} día${seleccion.size === 1 ? '' : 's'} bloqueado${seleccion.size === 1 ? '' : 's'}${horas} en ${agendasSel.size} agenda${agendasSel.size === 1 ? '' : 's'} — ${motivoFinal}`);
      setSeleccion(new Set());
      await trasCambiar();
      return r;
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudieron crear los bloqueos'));
      return undefined;
    } finally {
      setGuardando(false);
    }
  };

  const desbloquear = async (b) => {
    const horas = b.all_day === false ? ` de ${b.start} a ${b.end}` : '';
    if (!window.confirm(`¿Quitar el bloqueo de ${nombreAgenda[b.agenda_id] || 'la agenda'} (${formatDate(b.date_from)}${b.date_to !== b.date_from ? ` – ${formatDate(b.date_to)}` : ''}${horas})?`)) return;
    try {
      await deleteBloqueo(b.id);
      toast.success('Bloqueo quitado');
      await trasCambiar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo quitar el bloqueo'));
    }
  };

  const hoyStr = ymd(hoy);

  return (
    <section className="rounded-xl border border-[#D9E3DD] bg-white p-3 sm:p-4" data-testid="bloqueos-panel">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-[#13231E] flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#b91c1c]" /> Bloquear agendas: días enteros o unas horas
        </h2>
        <p className="text-xs text-muted-foreground">
          Festivos, congresos, una tarde de reunión… de una agenda, de varias o de todas.
          Las vacaciones y los días libres de un doctor se marcan en su calendario, arriba.
        </p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4 min-w-0">
          {/* Calendario: clic para marcar/desmarcar días */}
          <div className="rounded-lg border border-border p-3">
            <div className="flex items-center justify-between mb-2">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                onClick={() => setMes((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                aria-label="Mes anterior" data-testid="bloqueos-mes-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-sm font-semibold text-[#13231E] capitalize" data-testid="bloqueos-mes">
                {MESES[mes.getMonth()]} {mes.getFullYear()}
              </div>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                onClick={() => setMes((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                aria-label="Mes siguiente" data-testid="bloqueos-mes-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground mb-1">
              {DIAS_SEMANA.map((d) => <div key={d}>{d}</div>)}
            </div>
            {semanas.map((sem, i) => (
              <div key={i} className="grid grid-cols-7 gap-1 mb-1">
                {sem.map((d, j) => {
                  if (!d) return <div key={j} />;
                  const f = ymd(d);
                  const sel = seleccion.has(f);
                  const yaBloqueado = bloqueadosDelMes.has(f);
                  const pasado = f < hoyStr;
                  return (
                    <button
                      key={j}
                      type="button"
                      disabled={pasado}
                      onClick={() => alternarDia(f)}
                      data-testid={`bloqueos-dia-${f}`}
                      aria-pressed={sel}
                      title={yaBloqueado ? 'Este día ya tiene algún bloqueo' : ''}
                      className={`h-9 rounded-md text-sm tabular-nums transition-colors relative
                        ${pasado ? 'text-muted-foreground/40 cursor-not-allowed'
                          : sel ? 'bg-[#b91c1c] text-white font-semibold'
                          : 'hover:bg-[#b91c1c]/10'}
                        ${f === hoyStr && !sel ? 'ring-1 ring-[#2E6E5E]/50' : ''}`}
                    >
                      {d.getDate()}
                      {yaBloqueado && !sel && (
                        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#b91c1c]" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground mt-1">
              Marca días sueltos o seguidos (los seguidos se agrupan solos). El punto rojo: día con algún bloqueo ya puesto.
            </p>
          </div>
        </div>

        <div className="space-y-4 min-w-0">
          {/* Agendas afectadas */}
          <div>
            <Label>¿A qué agendas aplica?</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setAgendasSel((prev) => (prev.size === agendas.length ? new Set() : new Set(agendas.map((a) => a.id))))}
                data-testid="bloqueos-todas"
                className={`px-2.5 py-1 rounded-full border text-xs font-medium ${
                  agendasSel.size === agendas.length && agendas.length > 0
                    ? 'border-[#b91c1c] bg-[#b91c1c]/10 text-[#b91c1c]' : 'border-border'}`}
              >
                Todas
              </button>
              {idsDestacadas.length > 0 && (
                <button
                  type="button"
                  onClick={() => setAgendasSel(sonLasDestacadas ? new Set() : new Set(idsDestacadas))}
                  data-testid="bloqueos-del-doctor"
                  className={`px-2.5 py-1 rounded-full border text-xs font-medium ${
                    sonLasDestacadas ? 'border-[#b91c1c] bg-[#b91c1c]/10 text-[#b91c1c]' : 'border-border'}`}
                >
                  Las de {destacadas.nombre}
                </button>
              )}
              {agendas.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => alternarAgenda(a.id)}
                  data-testid={`bloqueos-agenda-${a.id}`}
                  aria-pressed={agendasSel.has(a.id)}
                  className={`px-2.5 py-1 rounded-full border text-xs font-medium ${
                    agendasSel.has(a.id) ? 'border-[#b91c1c] bg-[#b91c1c]/10 text-[#b91c1c]' : 'border-border'}`}
                >
                  {a.name}
                </button>
              ))}
            </div>
          </div>

          {/* Día entero o unas horas */}
          <div>
            <label className="inline-flex items-center gap-2 text-sm text-[#13231E]">
              <input
                type="checkbox"
                checked={diaEntero}
                onChange={(e) => setDiaEntero(e.target.checked)}
                className="h-4 w-4 accent-[#b91c1c]"
                data-testid="bloqueos-dia-entero"
              />
              Día entero
            </label>
            {!diaEntero && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="bloqueos-start">Desde las</Label>
                  <Input id="bloqueos-start" type="time" value={start} className="mt-1.5 tabular-nums"
                    onChange={(e) => setStart(e.target.value)} data-testid="bloqueos-start" />
                </div>
                <div>
                  <Label htmlFor="bloqueos-end">Hasta las</Label>
                  <Input id="bloqueos-end" type="time" value={end} className="mt-1.5 tabular-nums"
                    onChange={(e) => setEnd(e.target.value)} data-testid="bloqueos-end" />
                </div>
              </div>
            )}
          </div>

          {/* Motivo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label>Motivo</Label>
              <Select value={motivo} onValueChange={setMotivo}>
                <SelectTrigger data-testid="bloqueos-motivo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOTIVOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  <SelectItem value="__otro__">Otro motivo…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {motivo === '__otro__' && (
              <div>
                <Label>Escribe el motivo</Label>
                <Input value={motivoLibre} onChange={(e) => setMotivoLibre(e.target.value)}
                  placeholder="Obras, formación…" data-testid="bloqueos-motivo-libre" />
              </div>
            )}
          </div>

          {/* En el móvil el texto es largo («Bloquear 3 días de 15:00 a 20:00
              en 2 agendas»): parte línea en vez de salirse del botón. */}
          <Button
            onClick={bloquear}
            disabled={guardando || !seleccion.size || !agendasSel.size || !motivoFinal || horasMal}
            className="w-full h-auto min-h-10 py-2 whitespace-normal text-center leading-snug bg-[#b91c1c] hover:bg-[#991b1b] text-white"
            data-testid="bloqueos-guardar"
          >
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
            Bloquear {seleccion.size || ''} día{seleccion.size === 1 ? '' : 's'}
            {diaEntero ? '' : ` de ${start} a ${end}`}
            {agendasSel.size ? ` en ${agendasSel.size} agenda${agendasSel.size === 1 ? '' : 's'}` : ''}
          </Button>
        </div>
      </div>

      {/* Bloqueos vigentes */}
      <div className="mt-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
          Bloqueos vigentes y futuros
        </div>
        {cargandoExistentes ? (
          <div className="py-3 text-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando…
          </div>
        ) : existentes.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="bloqueos-ninguno">No hay bloqueos a partir de hoy.</p>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {existentes.map((b) => (
              <div key={b.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`bloqueo-fila-${b.id}`}>
                <Lock className="w-3.5 h-3.5 text-[#b91c1c] shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="font-medium">{nombreAgenda[b.agenda_id] || 'Agenda'}</span>
                  <span className="text-muted-foreground"> · {formatDate(b.date_from)}{b.date_to !== b.date_from ? ` – ${formatDate(b.date_to)}` : ''}</span>
                  <span className="text-muted-foreground"> · {textoHoras(b)}</span>
                  {b.reason ? <span className="text-muted-foreground"> · {b.reason}</span> : null}
                </div>
                <button
                  type="button"
                  onClick={() => desbloquear(b)}
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  title="Quitar el bloqueo"
                  aria-label="Quitar el bloqueo"
                  data-testid={`bloqueo-quitar-${b.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
